import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount, formatRate } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';
import { CHARGEBACK_CEILING_BP, formatDay, type AffiliateRow } from './types.ts';

/**
 * Close a quality review.
 *
 * The counterpart to the `affiliate.quality-review` worker job. The job OPENS a
 * review when 30-day FTD conversion falls under the configured floor; only a
 * person closes one, because a review is a judgement about traffic quality and
 * a scheduled job is nobody — there is no admin to record as having made the
 * finding. Without this dialog the trigger would be a trap: an affiliate would
 * enter REVIEW and have no way out of it.
 *
 * The two outcomes are not symmetrical, and the copy says so. CLEARED returns
 * the affiliate to ACTIVE and clears the flag. SUSPENDED stops NEW attribution
 * and does NOT stop the affiliate earning: the players it already sent keep
 * earning revshare unless that is separately clawed back. Operators get this
 * wrong, so it is stated at the point of decision rather than in a rules panel.
 */

const OUTCOMES: readonly { value: 'CLEARED' | 'SUSPENDED'; label: string }[] = [
  { value: 'CLEARED', label: 'Cleared — traffic is acceptable, return to active' },
  { value: 'SUSPENDED', label: 'Suspended — stop attributing new players' },
];

export function QualityReviewDialog({
  affiliate,
  floorBp,
  floorIsConfigured,
  onClose,
  onDone,
}: {
  affiliate: AffiliateRow | null;
  floorBp: number;
  /** False when the thresholds read failed and the fallback is being shown. */
  floorIsConfigured: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [outcome, setOutcome] = useState<'CLEARED' | 'SUSPENDED'>('CLEARED');
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const close = () => {
    setOutcome('CLEARED');
    setNote('');
    setFields({});
    onClose();
  };

  const submit = async () => {
    if (affiliate === null) return;
    if (note.trim().length < 3) {
      setFields({ note: 'Record what the review found.' });
      return;
    }
    setBusy(true);
    try {
      const result = await api.post<{ ref: string; status: string; message: string }>(
        `/api/admin/affiliates/${affiliate.id}/quality-review`,
        { outcome, note: note.trim() },
      );
      toast(result.message);
      close();
      onDone();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: `Could not close the review on ${affiliate.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The review is still open. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={affiliate !== null}
      onClose={close}
      title={affiliate === null ? '' : `Quality review · ${affiliate.ref}`}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            Record outcome
          </Button>
        </>
      }
    >
      {affiliate && (
        <div className="stack">
          <p style={{ margin: 0 }}>
            {/* THE COLUMN BEHIND THIS BRANCH IS `affiliate.quality_review_opened_at`, named in
                here and not on the screen. The distinction it draws is worth stating: a review
                with no opened date was not opened by the worker job, so there is no measurement
                behind it and nothing for the operator to read back. The name of the column is no
                use to them — they cannot look it up, ask anybody for it, or repeat it to somebody
                who would act on it — and "no date is recorded for when the review opened" is the
                same fact in words they already have. The detail drawer shows that same date under
                "Quality review opened", which reads "never" in exactly this case. */}
            {affiliate.quality_review_opened_at === null ? (
              <>
                {affiliate.name} is under review, and no date is recorded for when the review opened
                — so this review was not opened by the automatic trigger. There is no recorded
                reason for it beyond the status itself.
              </>
            ) : (
              <>
                A review opened on {formatDay(affiliate.quality_review_opened_at)} because
                {affiliate.conversion_bp_30d === null ? (
                  <> FTD conversion fell below the configured floor.</>
                ) : (
                  <>
                    {' '}
                    30-day FTD conversion was{' '}
                    <strong>{formatRate(Number(affiliate.conversion_bp_30d))}</strong>, below the{' '}
                    {formatRate(floorBp)} floor
                    {floorIsConfigured ? '' : ' (shown from the fallback — the settings read failed)'}
                    .
                  </>
                )}
              </>
            )}
          </p>

          <dl className="stack" style={{ margin: 0 }}>
            <div className="def-row">
              <dt>Signups · FTDs, 30 days</dt>
              <dd>
                {formatCount(Number(affiliate.signups_30d))} ·{' '}
                {formatCount(Number(affiliate.ftd_30d))}
              </dd>
            </div>
            <div className="def-row">
              <dt>FTD conversion, 30 days</dt>
              <dd>
                {affiliate.conversion_bp_30d === null
                  ? '— no signups in the window'
                  : formatRate(Number(affiliate.conversion_bp_30d))}
              </dd>
            </div>
            <div className="def-row">
              <dt>Chargeback rate</dt>
              {/* Not a zero, and not blank. The rule's other half has no source
                  at all, and a dash with no explanation reads as "none". */}
              <dd className="text-subtle">not measured — no chargeback rate is computed</dd>
            </div>
          </dl>

          <Field label="Outcome">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as 'CLEARED' | 'SUSPENDED')}
              >
                {OUTCOMES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {outcome === 'SUSPENDED' && (
            <div className="notice">
              <Icon name="warning-circle" />
              <div>
                <strong>Suspending stops new attribution only.</strong> The{' '}
                {formatCount(Number(affiliate.referred_players))} players already attributed to{' '}
                {affiliate.name} keep earning revshare, and this affiliate keeps accruing
                commission on them, unless that is separately clawed back. It is not an off switch.
              </div>
            </div>
          )}

          <Field
            label="What the review found"
            error={fields['note']}
            hint="Recorded in the audit trail. On a suspension it is also stored as the suspension reason."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setFields({});
                }}
                placeholder="Traffic sampled — genuine players, seasonal dip in deposits"
              />
            )}
          </Field>

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Only the FTD-conversion half of the rule can open a review. The{' '}
              <em>chargebacks above {formatRate(CHARGEBACK_CEILING_BP)}</em> half has no rate to
              measure against — no payment provider reports chargebacks or disputes to the
              platform, and nothing turns the chargebacks staff record by hand into a rate — so
              nothing evaluates it, here or anywhere else.
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
